import { isEmpty, isNil, isNumber } from 'lodash-es';
import { useObservable, useObservableState } from 'observable-hooks';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { inlineFieldGroupClasses } from './fields/sharedComponents';
import InformationTooltip from './InformationTooltip';
import { LayoutTree } from './layout-helper';
import { useSharedFormContext } from './utils';
import LocationField from './fields/LocationField';
import ProjectField from './fields/ProjectField';
import { SelfDefinedAlarmLineField } from './fields/SelfDefinedAlarmLineField';
import SoundDailyValueSettingsField from './fields/soundDailyValueSettingsField/SoundDailyValueSettingsField';
import WifiPasswordField from './fields/WifiPasswordField';
import LdenGuidelinePreviewField from './fields/LdenGuidelinePreviewField';
import { NotificationConfigField } from './fields/notificationConfigField/NotificationConfigField';
import { ScheduleField } from './fields/scheduleField/ScheduleField';
import { SwarmFormController } from '../pages/measuringPoint/form-controller';
import { FieldComponentRegistryProvider } from './fieldComponentRegistry';
import { useFormFieldComponentResolver } from './fieldComponentResolverContext';

const defaultFormFieldOverrides = {
    location: LocationField,
    project: ProjectField,
    selfDefinedAlarmLine: SelfDefinedAlarmLineField,
    dailyValueSettings: SoundDailyValueSettingsField,
    deviceWifiPassword: WifiPasswordField,
    ldenGuidelinePreview: LdenGuidelinePreviewField,
    notificationConfig: NotificationConfigField,
    schedule: ScheduleField,
};

function useGroupHiddenByConfigHelper(fieldNames) {
    const { formController } = useSharedFormContext();

    return useObservableState(
        useObservable(
            (inputs$) =>
                inputs$.pipe(
                    switchMap(([iFieldNames, iConfigHelper]) => {
                        if (!iConfigHelper.isEnabledForField(iFieldNames)) {
                            // Return original options.
                            return of(false);
                        }

                        return iConfigHelper.availableFields$.pipe(
                            // Check if at least one field name is available;
                            // if not, mark group as hidden.
                            map((availableFields) => {
                                const shouldBeVisible = fieldNames.some(
                                    (fieldName) => fieldName in availableFields
                                );
                                return !shouldBeVisible;
                            }),
                            // Avoid unnecessary emissions/rerenders.
                            distinctUntilChanged()
                        );
                    })
                ),
            [fieldNames, formController.configHelper]
        ),
        false
    );
}

export function PlainGroupContainer({ children }) {
    return children;
}
PlainGroupContainer.propTypes = {
    children: PropTypes.node.isRequired,
};

function MeasuringPointGroupContainer({ children, level, name, title, info }) {
    const Level = `h${level}`;
    const isRootLevel = level === 2;

    const header = title && (
        <Level className={isRootLevel ? 'mb-3.5 font-bold' : ''}>
            {title}
            {info && (
                <InformationTooltip
                    information={info}
                    className="text-form-text ml-2 align-middle"
                />
            )}
        </Level>
    );

    return (
        <>
            {!isRootLevel ? (
                <div>
                    {header}
                    {children}
                </div>
            ) : (
                <div className="odd:bg-main-background-odd">
                    <div className="main-container-new mx-auto py-10" id={name}>
                        {header}
                        {children}
                    </div>
                </div>
            )}
        </>
    );
}
MeasuringPointGroupContainer.propTypes = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string,
    info: PropTypes.string,
    level: PropTypes.number.isRequired,
    children: PropTypes.node.isRequired,
};

function Group({ group, GroupContainer, level = 2 }) {
    const { formController } = useSharedFormContext();
    const resolveFieldComponent = useFormFieldComponentResolver();
    const { name, title, fields, groups } = group;

    const fieldNames = useMemo(() => group.getFieldNames(), [group]);
    const hidden = useGroupHiddenByConfigHelper(fieldNames);

    const info = useMemo(
        () => formController.information?.[`_group_${name}`],
        [name, formController.information]
    );

    // We don't show empty groups.
    if (isEmpty(groups) && isEmpty(fields)) {
        return null;
    }

    if (hidden) {
        return null;
    }

    return (
        <GroupContainer level={level} name={name} title={title} info={info}>
            {!isEmpty(groups) ? (
                // If this group contains more groups, create sub groups.
                <div className="flex flex-col gap-y-2.5">
                    {groups.map((g) => (
                        <Group
                            key={g.name}
                            group={g}
                            GroupContainer={GroupContainer}
                            level={level + 1}
                        />
                    ))}
                </div>
            ) : (
                // If this group did not contain sub groups, create the form fields.
                <ul className={inlineFieldGroupClasses}>
                    {fields.map((field) => {
                        const FormField = resolveFieldComponent(field.type, field.name);

                        return (
                            <FormField
                                key={field.name}
                                fieldOptions={{
                                    name: field.name,
                                    settings: field,
                                }}
                            />
                        );
                    })}
                </ul>
            )}
        </GroupContainer>
    );
}

Group.propTypes = {
    group: PropTypes.instanceOf(LayoutTree),
    GroupContainer: PropTypes.func.isRequired,
    level(props, propName, componentName) {
        const given = props[propName];
        if (!isNil(given) && (!isNumber(given) || given < 2)) {
            return new Error(
                `Invalid prop \`${propName}\` supplied to` +
                    ` \`${componentName}\`. \`${propName}\`` +
                    ` should be 2 or more, but got \`${given}\`.`
            );
        }
        return null;
    },
};

export function Layout({ formController, GroupContainer }) {
    const formFieldOverrides =
        formController.createFormFieldOverrides() ?? defaultFormFieldOverrides;
    const treeWithFields = formController.createLayoutTree();
    const ResolvedGroupContainer = GroupContainer ?? MeasuringPointGroupContainer;

    return (
        <FieldComponentRegistryProvider formFieldOverrides={formFieldOverrides}>
            {treeWithFields.groups.map((group) => (
                <Group key={group.name} group={group} GroupContainer={ResolvedGroupContainer} />
            ))}
        </FieldComponentRegistryProvider>
    );
}
Layout.propTypes = {
    formController: PropTypes.instanceOf(SwarmFormController),
    GroupContainer: PropTypes.func,
};
